// src/components/SuggestionModal.tsx

import React, { useState } from 'react';
import styled from 'styled-components';

interface SuggestionModalProps {
    onClose: () => void;
}

const SuggestionModal: React.FC<SuggestionModalProps> = ({ onClose }) => {
    const [clues, setClues] = useState<string[]>(['', '', '', '', '']);
    const [answer, setAnswer] = useState('');
    const [error, setError] = useState('');

    const handleClueChange = (index: number, value: string) => {
        const updatedClues = [...clues];
        updatedClues[index] = value;
        setClues(updatedClues);
    };

    const handleSubmit = () => {
        // Gather how many clues are actually filled
        const filledClues = clues.filter((clue) => clue.trim() !== '');
        // Require at least 4 clues and an answer
        if (filledClues.length < 4 || answer.trim() === '') {
            setError('Please enter at least 4 clues and an answer.');
            return;
        }

        // Subject line and recipient
        const subject = encodeURIComponent('[Riddle Ravel Clue Suggestion]');
        const emailAddress = 'oysterbaygames@gmail.com';

        // Build the body of the email in plain text
        let body = `Hello OysterBayGames,\n\n`;
        body += `I hope you are doing well! I'd like to submit the following clues and answer for a future game on Riddle Ravel.\n\n`;
        body += `Clues:\n`;
        filledClues.forEach((clue, index) => {
            body += `   ${index + 1}. ${clue}\n`;
        });

        body += `\nProposed Answer: ${answer.trim()}\n\n`;
        body += `Best regards,\nA Riddle Ravel Fan\n\n`;
        body += `___________________________________________________\n`;
        body += `(If you have any questions, feel free to reply!)\n`;

        // Construct the mailto link
        const mailtoLink = `mailto:${emailAddress}?subject=${subject}&body=${encodeURIComponent(
            body
        )}`;

        // Open user’s email client with the populated message
        window.location.href = mailtoLink;

        // Close the modal after opening the email client
        onClose();
    };

    return (
        <Overlay onClick={onClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
                <Title>Riddle Ravel Clue Suggestion</Title>
                <Description>
                    Submit at least 4 clues and 1 answer you’d like to suggest for a future riddle ravel.
                </Description>
                <Form>
                    {clues.map((clue, index) => (
                        <StyledInput
                            key={index}
                            type="text"
                            placeholder={`Clue ${index + 1}`}
                            value={clue}
                            onChange={(e) => handleClueChange(index, e.target.value)}
                        />
                    ))}
                    <StyledInput
                        type="text"
                        placeholder="Proposed Answer"
                        value={answer}
                        onChange={(e) => setAnswer(e.target.value)}
                    />
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                    <Button onClick={handleSubmit}>Submit</Button>
                </Form>
                <CloseButton onClick={onClose} aria-label="Close">&times;</CloseButton>
            </ModalContainer>
        </Overlay>
    );
};

/*
  STYLED COMPONENTS
*/

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
`;

const ModalContainer = styled.div`
    background: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.text};
    padding: 20px;
    border-radius: 8px;
    max-width: 480px;
    width: 90%;
    position: relative;
    box-sizing: border-box;
    box-shadow: 0 8px 16px rgba(0,0,0,0.3);

    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Title = styled.h2`
    margin: 0;
    text-align: center;
`;

const Description = styled.p`
    margin: 0;
    text-align: center;
`;

const Form = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const StyledInput = styled.input`
    padding: 12px;
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.borderColor};
    background: ${({ theme }) => theme.tileEmpty};
    color: ${({ theme }) => theme.text};
    font-size: 1rem;
    width: 100%;
    box-sizing: border-box;

    &:focus {
        outline: 2px solid ${({ theme }) => theme.accentColor};
    }
`;

const ErrorMessage = styled.p`
    color: #dc3545;
    font-size: 0.95rem;
    margin: 0 auto;
    text-align: center;
`;

const Button = styled.button`
    background: ${({ theme }) => theme.accentColor};
    color: ${({ theme }) => theme.text};
    border: none;
    border-radius: 6px;
    padding: 12px;
    font-weight: bold;
    cursor: pointer;
    margin-top: 4px;

    &:hover {
        background: #6aaa64;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.text};
    font-size: 1.5rem;
    cursor: pointer;
    position: absolute;
    top: 12px;
    right: 12px;

    &:hover {
        color: ${({ theme }) => theme.accentColor};
    }
`;

export default SuggestionModal;
